/**
 * kirja-wishlist-sync.js
 *
 * Restores the "added" state of the hand-placed Advanced Wishlist (HulkApps)
 * heart button rendered in snippets/kirja-price-button.liquid.
 *
 * Why this exists:
 * The app only hydrates the buttons IT renders (inside its app blocks). Our
 * copied `.wishlist-btn` gets a hardcoded `data-added="false"` in Liquid and is
 * never restored on page load, even though clicking it works (the app handles
 * clicks via delegation on `document`).
 *
 * Strategy (app-agnostic, no dependency on the app's internal storage):
 *  1. Whenever the app changes `data-added` on any of our buttons (add/remove),
 *     we persist that truth to localStorage, keyed by product id.
 *  2. On load — and whenever new product cards are injected (filters,
 *     pagination, quick view) — we re-apply the cached "true" state so the
 *     filled heart persists across reloads.
 */
(function () {
  "use strict";

  if (window.__kirjaWishlistSync) return;
  window.__kirjaWishlistSync = true;

  var STORAGE_KEY = "kirja:wishlist:v1";
  var BTN_SELECTOR = ".wishlist-btn[data-proid]";

  function readCache() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  }

  function writeCache(cache) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
    } catch (e) {
      /* storage unavailable / full — ignore */
    }
  }

  function isAdded(btn) {
    return String(btn.getAttribute("data-added")) === "true";
  }

  /** Persist the current button state as the source of truth. */
  function persist(btn) {
    var id = btn.getAttribute("data-proid");
    if (!id) return;
    var cache = readCache();
    if (isAdded(btn)) {
      cache[id] = true;
    } else {
      delete cache[id];
    }
    writeCache(cache);
  }

  /** Apply cached "added" state to a single button. */
  function restore(btn, cache) {
    var id = btn.getAttribute("data-proid");
    if (id && cache[id] && !isAdded(btn)) {
      btn.setAttribute("data-added", "true");
    }
  }

  /** Watch a button's data-added attribute so app changes are persisted. */
  var attrObserver = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      persist(mutations[i].target);
    }
  });

  function observeBtn(btn) {
    if (btn.__kirjaWishlistObserved) return;
    btn.__kirjaWishlistObserved = true;
    attrObserver.observe(btn, {
      attributes: true,
      attributeFilter: ["data-added"],
    });
  }

  function processButtons(root) {
    var cache = readCache();
    var scope = root && root.querySelectorAll ? root : document;
    var buttons = scope.querySelectorAll(BTN_SELECTOR);
    for (var i = 0; i < buttons.length; i++) {
      restore(buttons[i], cache);
      observeBtn(buttons[i]);
    }
  }

  function init() {
    processButtons(document);

    // Re-apply state to product cards injected after load
    // (collection filters, pagination, predictive search, quick view, etc.)
    var domObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(BTN_SELECTOR)) {
            var cache = readCache();
            restore(node, cache);
            observeBtn(node);
          } else if (node.querySelector && node.querySelector(BTN_SELECTOR)) {
            processButtons(node);
          }
        }
      }
    });
    domObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
